import { useState, useEffect, useCallback, useRef } from 'react';
import { brand, space, fontFamily } from '../brand/theme';
import { CONFIG } from '../lib/config';

// Types

interface BatchJob {
  id: string;
  status: 'queued' | 'processing' | 'done' | 'failed' | 'cancelled';
  job_type: 'chat' | 'ocr';
  model: string;
  input_type: 'inline' | 'drive';
  filename: string | null;
  input_ref: string | null;
  priority: number;
  retry_count: number;
  result: { content: string; usage: { total_tokens?: number; prompt_tokens?: number; completion_tokens?: number }; model: string } | null;
  error: string | null;
  drive_folder_id: string | null;
  drive_result_file_id: string | null;
  pipeline: string | null;
  pipeline_instruction: string | null;
  parent_job_id: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

interface OcrRegion {
  bbox: [number, number, number, number];
  type: string;
  text: string;
  page?: number;
}

const MODELS = [
  { id: 'MamayLM-Gemma-3-27B-IT-v2.0', label: 'Мамай 27B' },
  { id: 'LapaLLM-Gemma-3-12B-instruct', label: 'Лапа 12B' },
];

const SERVICE_ACCOUNT_EMAIL = 'batch-worker@lapathoniia-prod.iam.gserviceaccount.com';

// Helpers

function timeAgo(ts: string): string {
  const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (diff < 60) return `${diff}с тому`;
  if (diff < 3600) return `${Math.floor(diff / 60)}хв тому`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}год тому`;
  return `${Math.floor(diff / 86400)}д тому`;
}

function parseFolderIdFromUrl(input: string): string {
  // Accept full URL or bare ID
  const match = input.match(/\/folders\/([a-zA-Z0-9_-]+)/);
  return match ? match[1] : input.trim();
}

function statusIcon(status: BatchJob['status']): string {
  switch (status) {
    case 'queued': return '⏳';
    case 'processing': return '🔄';
    case 'done': return '✅';
    case 'failed': return '❌';
    case 'cancelled': return '🚫';
  }
}

function statusLabel(status: BatchJob['status']): string {
  switch (status) {
    case 'queued': return 'Черга';
    case 'processing': return 'Обробка';
    case 'done': return 'Готово';
    case 'failed': return 'Помилка';
    case 'cancelled': return 'Скасовано';
  }
}

function statusColor(status: BatchJob['status']): string {
  switch (status) {
    case 'done': return brand.tealLight;
    case 'failed': return brand.red;
    case 'processing': return brand.gold;
    case 'cancelled': return brand.creamSubtle;
    default: return brand.creamMuted;
  }
}

function shortFilename(job: BatchJob): string {
  if (job.filename) return job.filename;
  if (job.input_type === 'inline') return '📝 Inline';
  if (job.job_type === 'ocr') return '📷 Зображення';
  return '📄 Документ';
}

function formatTokens(n?: number): string {
  if (!n) return '—';
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}

// Result Viewer

function ResultViewer({ job, allJobs, onClose }: { job: BatchJob; allJobs: BatchJob[]; onClose: () => void }) {
  const result = job.result;
  const content = result?.content ?? '';
  const usage = result?.usage ?? {};

  // For pipeline parent (OCR job with pipeline='ocr_then_chat'):
  // find the child LLM job
  const childJob = job.pipeline === 'ocr_then_chat'
    ? allJobs.find(j => j.parent_job_id === job.id)
    : null;

  // For pipeline child (chat job with parent_job_id set):
  // find the parent OCR job
  const parentJob = job.parent_job_id
    ? allJobs.find(j => j.id === job.parent_job_id)
    : null;

  // Determine what to show
  const isPipelineParent = job.pipeline === 'ocr_then_chat';
  const isPipelineChild = !!job.parent_job_id;

  let regions: OcrRegion[] | null = null;
  let chatText = content;

  if (job.job_type === 'ocr') {
    try {
      const parsed = JSON.parse(content);
      if (Array.isArray(parsed)) regions = parsed;
    } catch {
      chatText = content;
    }
  }

  const downloadJSON = () => {
    const payload = isPipelineParent && childJob?.result
      ? { ocr: result, llm: childJob.result, pipeline_instruction: job.pipeline_instruction }
      : result;
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `batch-result-${job.id.slice(0, 8)}.json`;
    a.click();
  };

  const downloadTXT = () => {
    let text = '';
    if (isPipelineParent && childJob?.result) {
      // Combined: OCR text + LLM answer
      if (regions) text += regions.filter(r => r.text).map(r => r.text).join('\n');
      text += '\n\n--- LLM відповідь ---\n';
      text += childJob.result.content;
    } else if (regions) {
      text = regions.filter(r => r.text).map(r => r.text).join('\n');
    } else {
      text = chatText;
    }
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `batch-result-${job.id.slice(0, 8)}.txt`;
    a.click();
  };

  // Don't show pipeline child jobs directly — they're shown inside the parent
  const title = isPipelineParent
    ? '🔗 OCR + LLM pipeline'
    : isPipelineChild
    ? '💬 LLM відповідь (pipeline)'
    : job.job_type === 'ocr' ? '📷 OCR результат' : '💬 Chat результат';

  const panelStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0, right: 0, bottom: 0,
    width: '520px',
    maxWidth: '100vw',
    background: brand.bgCard,
    borderLeft: `1px solid ${brand.border}`,
    display: 'flex',
    flexDirection: 'column',
    zIndex: 1000,
    boxShadow: '-4px 0 24px rgba(0,0,0,0.4)',
  };

  const totalTokens = (usage.total_tokens ?? 0) + (childJob?.result?.usage?.total_tokens ?? 0);

  return (
    <div style={panelStyle}>
      {/* Header */}
      <div style={{ padding: '14px 16px', borderBottom: `1px solid ${brand.border}`, display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        <button onClick={onClose} style={{ border: 'none', background: 'none', color: brand.creamMuted, fontSize: '20px', cursor: 'pointer', padding: '2px 6px', lineHeight: 1 }}>←</button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: brand.cream, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {job.filename || title}
          </div>
          <div style={{ fontSize: '11px', color: brand.creamSubtle, marginTop: 2 }}>
            {title} · {job.model.replace('MamayLM-Gemma-3-', 'Мамай ').replace('LapaLLM-Gemma-3-', 'Лапа ').replace('-IT-v2.0','').replace('-IT-v1.0','').replace('-instruct','')} · {formatTokens(totalTokens)} токенів
            {job.completed_at && ` · ${timeAgo(job.completed_at)}`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={downloadTXT} style={dlBtn}>⬇ TXT</button>
          <button onClick={downloadJSON} style={dlBtn}>⬇ JSON</button>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px' }}>
        {job.status === 'failed' ? (
          <div style={{ padding: '12px', background: brand.errorMuted, border: `1px solid ${brand.red}`, borderRadius: '8px', color: brand.red, fontSize: '13px' }}>
            <strong>Помилка:</strong><br />
            <span style={{ fontFamily: 'monospace', fontSize: '11px', whiteSpace: 'pre-wrap' }}>{job.error}</span>
          </div>
        ) : (
          <>
            {/* OCR section */}
            {regions && (
              <div style={{ marginBottom: isPipelineParent ? 16 : 0 }}>
                {isPipelineParent && (
                  <div style={{ fontSize: '11px', color: brand.tealLight, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span>📷 OCR — Rukopys</span>
                    <span style={{ fontWeight: 400, color: brand.creamSubtle }}>({regions.filter(r => r.text).length} регіонів)</span>
                  </div>
                )}
                {!isPipelineParent && (
                  <div style={{ fontSize: '11px', color: brand.creamSubtle, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.8px', fontWeight: 700 }}>
                    {regions.filter(r => r.text).length} розпізнаних регіонів
                    {(result as any)?.pages_processed > 1 && ` · ${(result as any).pages_processed} сторінок`}
                  </div>
                )}
                {regions.filter(r => r.text).map((r, i) => (
                  <div key={i} style={{
                    padding: '7px 10px', marginBottom: 3, borderRadius: '6px',
                    background: brand.bgMuted,
                    borderLeft: `3px solid ${r.type === 'formula' ? brand.gold : r.type === 'handwritten' ? brand.tealLight : brand.creamSubtle}`,
                  }}>
                    <div style={{ fontSize: '9px', color: brand.creamSubtle, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 2 }}>
                      {r.type}{(r.page ?? 1) > 1 ? ` · стор. ${r.page}` : ''}
                    </div>
                    <div style={{ fontSize: '13px', color: brand.cream, lineHeight: 1.5, fontFamily: r.type === 'formula' ? 'monospace' : fontFamily }}>{r.text}</div>
                  </div>
                ))}
              </div>
            )}

            {/* LLM pipeline result */}
            {isPipelineParent && (
              <div style={{
                padding: '12px',
                borderRadius: '8px',
                background: childJob ? brand.bgMuted : 'rgba(240,208,64,0.06)',
                border: `1px solid ${childJob ? brand.border : 'rgba(240,208,64,0.2)'}`,
              }}>
                <div style={{ fontSize: '11px', color: brand.gold, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 8 }}>
                  💬 LLM — {childJob?.model ?? 'очікується…'}
                  {job.pipeline_instruction && (
                    <span style={{ fontWeight: 400, color: brand.creamSubtle, textTransform: 'none', letterSpacing: 0, marginLeft: 6 }}>
                      "{job.pipeline_instruction.slice(0, 50)}{job.pipeline_instruction.length > 50 ? '…' : ''}"
                    </span>
                  )}
                </div>
                {!childJob && (
                  <div style={{ color: brand.gold, fontSize: '12px' }}>⏳ Обробляється…</div>
                )}
                {childJob?.status === 'failed' && (
                  <div style={{ color: brand.red, fontSize: '12px' }}>❌ {childJob.error}</div>
                )}
                {childJob?.status === 'done' && childJob.result && (
                  <div style={{ fontSize: '13px', color: brand.cream, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                    {childJob.result.content}
                  </div>
                )}
                {(childJob?.status === 'queued' || childJob?.status === 'processing') && (
                  <div style={{ color: brand.creamMuted, fontSize: '12px' }}>🔄 {statusLabel(childJob.status)}…</div>
                )}
              </div>
            )}

            {/* Pure chat result (non-pipeline) */}
            {!regions && !isPipelineParent && (
              <div style={{ fontSize: '13px', color: brand.cream, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                {chatText || <span style={{ color: brand.creamSubtle, fontStyle: 'italic' }}>Немає результату</span>}
              </div>
            )}
          </>
        )}
      </div>

      {/* Usage footer */}
      {(result || childJob?.result) && (
        <div style={{ padding: '10px 16px', borderTop: `1px solid ${brand.border}`, display: 'flex', gap: 16, flexShrink: 0 }}>
          {isPipelineParent ? (
            <>
              <div>
                <div style={{ fontSize: '9px', color: brand.creamSubtle, textTransform: 'uppercase', letterSpacing: '0.6px' }}>OCR токени</div>
                <div style={{ fontSize: '13px', color: brand.cream, fontWeight: 600, fontFamily: 'monospace' }}>{formatTokens(usage.total_tokens)}</div>
              </div>
              <div>
                <div style={{ fontSize: '9px', color: brand.creamSubtle, textTransform: 'uppercase', letterSpacing: '0.6px' }}>LLM токени</div>
                <div style={{ fontSize: '13px', color: brand.cream, fontWeight: 600, fontFamily: 'monospace' }}>{formatTokens(childJob?.result?.usage?.total_tokens)}</div>
              </div>
              <div>
                <div style={{ fontSize: '9px', color: brand.creamSubtle, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Всього</div>
                <div style={{ fontSize: '13px', color: brand.tealLight, fontWeight: 600, fontFamily: 'monospace' }}>{formatTokens(totalTokens)}</div>
              </div>
            </>
          ) : (
            [['Вхід', usage.prompt_tokens], ['Вихід', usage.completion_tokens], ['Всього', usage.total_tokens]].map(([label, val]) => (
              <div key={label as string}>
                <div style={{ fontSize: '9px', color: brand.creamSubtle, textTransform: 'uppercase', letterSpacing: '0.6px' }}>{label}</div>
                <div style={{ fontSize: '13px', color: brand.cream, fontWeight: 600, fontFamily: 'monospace' }}>{formatTokens(val as number)}</div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

const dlBtn: React.CSSProperties = {
  padding: '5px 10px', borderRadius: '6px',
  border: `1px solid ${brand.border}`,
  background: 'transparent', color: brand.creamMuted,
  fontSize: '11px', cursor: 'pointer', fontFamily,
  transition: 'border-color 150ms, color 150ms',
};

// Submit Form

function SubmitForm({ apiKey, onSubmitted }: { apiKey?: string; onSubmitted: (skipped?: string[]) => void }) {
  const [folderUrl, setFolderUrl] = useState('');
  const [model, setModel] = useState(MODELS[0].id);
  const [pipeline, setPipeline] = useState<'auto' | 'ocr_then_chat'>('auto');
  const [instruction, setInstruction] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const copyEmail = () => {
    navigator.clipboard.writeText(SERVICE_ACCOUNT_EMAIL).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const submit = async () => {
    const folderId = parseFolderIdFromUrl(folderUrl);
    if (!folderId) { setError('Введіть URL або ID папки Google Drive'); return; }
    setLoading(true);
    setError('');
    try {
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (apiKey) headers['Authorization'] = `Bearer ${apiKey}`;
      const body: Record<string, any> = {
        job_type: 'auto',
        model,
        drive: { folder_id: folderId },
      };
      if (pipeline === 'ocr_then_chat') {
        body.pipeline = 'ocr_then_chat';
        if (instruction.trim()) body.pipeline_instruction = instruction.trim();
      } else if (instruction.trim()) {
        // auto mode with instruction:
        // backend will apply it as pipeline for OCR files, system prompt for chat files
        body.pipeline_instruction = instruction.trim();
      }
      const res = await fetch(`${CONFIG.backendUrl}/batch/jobs`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || `Помилка ${res.status}`);
      } else {
        setFolderUrl('');
        setInstruction('');
        onSubmitted(data._skipped_files || []);
      }
    } catch (e: any) {
      setError(e.message || 'Мережева помилка');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '9px 12px',
    borderRadius: '8px', border: `1px solid ${brand.border}`,
    background: brand.bgMuted, color: brand.cream,
    fontSize: '13px', fontFamily, outline: 'none',
    boxSizing: 'border-box', transition: 'border-color 150ms',
  };

  return (
    <div style={{ padding: '16px', borderBottom: `1px solid ${brand.border}` }}>
      <div style={{ fontSize: '13px', fontWeight: 600, color: brand.cream, marginBottom: 12 }}>
        📁 Обробити папку Google Drive
      </div>

      {/* Step 1 — share folder */}
      <div style={{ marginBottom: 12, padding: '10px 12px', background: brand.bgMuted, borderRadius: '8px', border: `1px solid ${brand.border}` }}>
        <div style={{ fontSize: '11px', color: brand.creamSubtle, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 6 }}>
          Крок 1 — Поділіться папкою
        </div>
        <div style={{ fontSize: '12px', color: brand.creamMuted, marginBottom: 8, lineHeight: 1.6 }}>
          Відкрийте Google Drive → правий клік на папку → <strong style={{ color: brand.cream }}>Поділитися</strong> → додайте:
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <code style={{ flex: 1, fontSize: '10px', padding: '5px 8px', background: brand.bg, borderRadius: '5px', color: brand.tealLight, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
            {SERVICE_ACCOUNT_EMAIL}
          </code>
          <button
            onClick={copyEmail}
            style={{ ...dlBtn, flexShrink: 0, color: copied ? brand.tealLight : brand.creamMuted, borderColor: copied ? brand.tealLight : brand.border }}
          >
            {copied ? '✓' : '📋'}
          </button>
        </div>
        <div style={{ fontSize: '11px', color: brand.creamSubtle, marginTop: 5 }}>Роль: <strong style={{ color: brand.cream }}>Редактор</strong></div>
      </div>

      {/* Step 2 — folder URL */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: '11px', color: brand.creamSubtle, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 5 }}>
          Крок 2 — URL або ID папки
        </div>
        <input
          value={folderUrl}
          onChange={e => setFolderUrl(e.target.value)}
          placeholder="https://drive.google.com/drive/folders/... або ID"
          style={inputStyle}
          onFocus={e => e.currentTarget.style.borderColor = brand.tealLight}
          onBlur={e => e.currentTarget.style.borderColor = brand.border}
        />
      </div>

      {/* Pipeline mode */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: '11px', color: brand.creamSubtle, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 6 }}>
          Режим обробки
        </div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          {([
            ['auto', '🔍 Авто', 'OCR або chat залежно від файлу'],
            ['ocr_then_chat', '🔗 OCR + LLM', 'Спочатку розпізнати, потім обробити'],
          ] as const).map(([val, label, hint]) => (
            <button
              key={val}
              onClick={() => setPipeline(val)}
              title={hint}
              style={{
                flex: 1, padding: '7px 8px', borderRadius: '7px', fontSize: '11px',
                cursor: 'pointer', fontFamily, textAlign: 'center' as const,
                border: `1px solid ${pipeline === val ? brand.tealLight : brand.border}`,
                background: pipeline === val ? 'rgba(78,205,196,0.1)' : 'transparent',
                color: pipeline === val ? brand.tealLight : brand.creamMuted,
                transition: 'all 150ms',
              }}
            >{label}</button>
          ))}
        </div>

        {/* Instruction textarea — always shown */}
        <div>
          <div style={{ fontSize: '11px', color: brand.creamSubtle, marginBottom: 4 }}>
            {pipeline === 'ocr_then_chat'
              ? <>Завдання для LLM після OCR <span style={{ opacity: 0.6 }}>(необов'язково)</span></>
              : <>Завдання для всіх файлів <span style={{ opacity: 0.6 }}>(необов'язково)</span></>
            }
          </div>
          <textarea
            value={instruction}
            onChange={e => setInstruction(e.target.value)}
            placeholder="Наприклад: Витягни всі дати та імена. Перекажи суть документа. Знайди ключові цифри."
            rows={3}
            style={{
              ...inputStyle,
              resize: 'vertical', lineHeight: 1.5,
              minHeight: '72px',
            } as React.CSSProperties}
            onFocus={e => e.currentTarget.style.borderColor = brand.tealLight}
            onBlur={e => e.currentTarget.style.borderColor = brand.border}
          />
          <div style={{ fontSize: '10px', color: brand.creamSubtle, marginTop: 3 }}>
            {pipeline === 'ocr_then_chat'
              ? 'Застосовується до зображень/PDF після розпізнавання'
              : 'Для зображень/PDF: запускає OCR→LLM pipeline. Для тексту/CSV/Excel: додається як завдання до чату.'
            }
          </div>
        </div>
      </div>

      {/* Model picker */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: '11px', color: brand.creamSubtle, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 5 }}>
          {pipeline === 'ocr_then_chat' ? 'Модель для LLM кроку' : 'Модель для текстових файлів'}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {MODELS.map(m => (
            <button
              key={m.id}
              onClick={() => setModel(m.id)}
              style={{
                padding: '5px 10px', borderRadius: '6px', fontSize: '11px', cursor: 'pointer', fontFamily,
                border: `1px solid ${model === m.id ? brand.tealLight : brand.border}`,
                background: model === m.id ? 'rgba(78,205,196,0.1)' : 'transparent',
                color: model === m.id ? brand.tealLight : brand.creamMuted,
                transition: 'all 150ms',
              }}
            >{m.label}</button>
          ))}
        </div>
        <div style={{ fontSize: '10px', color: brand.creamSubtle, marginTop: 4 }}>
          {pipeline === 'ocr_then_chat'
            ? 'Зображення/PDF → Rukopys OCR → обрана модель'
            : 'Зображення та PDF завжди обробляються Rukopys OCR'}
        </div>
      </div>

      {error && (
        <div style={{ marginBottom: 8, padding: '7px 10px', borderRadius: '6px', background: brand.errorMuted, border: `1px solid ${brand.red}`, color: brand.red, fontSize: '12px' }}>
          {error}
        </div>
      )}

      <button
        onClick={submit}
        disabled={loading || !folderUrl.trim()}
        style={{
          width: '100%', padding: '9px', borderRadius: '8px', border: 'none',
          background: !loading && folderUrl.trim() ? brand.tealLight : brand.border,
          color: !loading && folderUrl.trim() ? brand.bg : brand.creamMuted,
          fontSize: '13px', fontWeight: 600, cursor: loading || !folderUrl.trim() ? 'not-allowed' : 'pointer',
          fontFamily, transition: 'background 150ms',
        }}
      >
        {loading ? '⏳ Надсилання…' : pipeline === 'ocr_then_chat' ? '🔗 OCR + LLM для папки' : '🚀 Обробити папку'}
      </button>
    </div>
  );
}

// Jobs List

function JobsList({
  jobs,
  loading,
  onSelect,
  selectedId,
  onRefresh,
}: {
  jobs: BatchJob[];
  loading: boolean;
  onSelect: (job: BatchJob) => void;
  selectedId: string | null;
  onRefresh: () => void;
}) {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [downloading, setDownloading] = useState(false);

  const pending = jobs.filter(j => j.status === 'queued' || j.status === 'processing').length;

  // Filter jobs by date range (client-side on already-loaded jobs)
  const filteredJobs = jobs.filter(j => {
    if (!j.parent_job_id === false) return false; // already handled by parent filter
    const d = new Date(j.created_at);
    if (dateFrom && d < new Date(dateFrom)) return false;
    if (dateTo && d > new Date(dateTo + 'T23:59:59')) return false;
    return true;
  }).filter(j => !j.parent_job_id); // hide pipeline children

  const doneJobs = filteredJobs.filter(j => j.status === 'done');

  const downloadAll = () => {
    if (doneJobs.length === 0) return;
    setDownloading(true);

    const rows = doneJobs.map(job => {
      const result = job.result;
      // For pipeline jobs, find the child LLM job result
      const childJob = job.pipeline === 'ocr_then_chat'
        ? jobs.find(j => j.parent_job_id === job.id)
        : null;

      let text = '';
      if (result?.content) {
        try {
          const regions = JSON.parse(result.content);
          if (Array.isArray(regions)) {
            text = regions.filter((r: any) => r.text).map((r: any) => r.text).join('\n');
          } else {
            text = result.content;
          }
        } catch {
          text = result.content;
        }
      }

      const row: any = {
        id: job.id,
        filename: job.filename || job.input_ref || '',
        job_type: job.job_type,
        model: job.model,
        pipeline: job.pipeline || null,
        status: job.status,
        created_at: job.created_at,
        completed_at: job.completed_at,
        tokens_total: (result?.usage?.total_tokens ?? 0) + (childJob?.result?.usage?.total_tokens ?? 0),
        tokens_prompt: result?.usage?.prompt_tokens ?? 0,
        tokens_completion: result?.usage?.completion_tokens ?? 0,
        result_text: text,
        result_raw: result?.content ?? '',
      };

      // Include LLM pipeline result if present
      if (childJob?.result) {
        row.llm_result = childJob.result.content;
        row.llm_model = childJob.model;
        row.pipeline_instruction = job.pipeline_instruction || null;
        row.tokens_llm = childJob.result.usage?.total_tokens ?? 0;
      }

      return row;
    });

    const blob = new Blob(
      [JSON.stringify({ exported_at: new Date().toISOString(), jobs_count: rows.length, jobs: rows }, null, 2)],
      { type: 'application/json' }
    );
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    const suffix = dateFrom || dateTo
      ? `_${dateFrom || 'start'}_${dateTo || 'end'}`
      : `_${new Date().toISOString().slice(0, 10)}`;
    a.download = `batch-results${suffix}.json`;
    a.click();
    setTimeout(() => { URL.revokeObjectURL(a.href); setDownloading(false); }, 1000);
  };

  const downloadCSV = () => {
    if (doneJobs.length === 0) return;
    const headers = ['filename', 'job_type', 'model', 'tokens_total', 'created_at', 'completed_at', 'result_text'];
    const rows = doneJobs.map(job => {
      const result = job.result;
      const childJob = job.pipeline === 'ocr_then_chat'
        ? jobs.find(j => j.parent_job_id === job.id)
        : null;
      let text = '';
      // For pipeline jobs, prefer LLM result; fall back to OCR text
      if (childJob?.result?.content) {
        text = childJob.result.content;
      } else if (result?.content) {
        try {
          const regions = JSON.parse(result.content);
          if (Array.isArray(regions)) text = regions.filter((r: any) => r.text).map((r: any) => r.text).join(' | ');
          else text = result.content;
        } catch { text = result.content; }
      }
      const totalTokens = (result?.usage?.total_tokens ?? 0) + (childJob?.result?.usage?.total_tokens ?? 0);
      return [
        job.filename || job.input_ref || '',
        job.job_type,
        job.model,
        totalTokens,
        job.created_at,
        job.completed_at || '',
        `"${text.replace(/"/g, '""').replace(/\n/g, ' ')}"`,
      ].join(',');
    });
    const blob = new Blob([[headers.join(','), ...rows].join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `batch-results_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const inputStyle: React.CSSProperties = {
    padding: '4px 8px', borderRadius: '5px',
    border: `1px solid ${brand.border}`,
    background: brand.bgMuted, color: brand.cream,
    fontSize: '11px', fontFamily, outline: 'none',
    colorScheme: 'dark',
  };

  return (
    <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {/* List header */}
      <div style={{ padding: '10px 16px', borderBottom: `1px solid ${brand.border}`, flexShrink: 0 }}>
        {/* Row 1: title + refresh */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: brand.cream }}>Завдання</span>
            {pending > 0 && (
              <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '10px', background: 'rgba(240,208,64,0.15)', color: brand.gold, border: `1px solid rgba(240,208,64,0.3)` }}>
                {pending} активних
              </span>
            )}
            {doneJobs.length > 0 && (
              <span style={{ fontSize: '10px', color: brand.creamSubtle }}>
                {doneJobs.length} готових
              </span>
            )}
          </div>
          <button
            onClick={onRefresh}
            disabled={loading}
            style={{ border: `1px solid ${brand.border}`, background: 'transparent', color: brand.creamMuted, fontSize: '11px', padding: '4px 8px', borderRadius: '5px', cursor: 'pointer', fontFamily, opacity: loading ? 0.5 : 1 }}
          >
            {loading ? '…' : '↻ Оновити'}
          </button>
        </div>

        {/* Row 2: date filter + download */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '10px', color: brand.creamSubtle }}>від</span>
          <input
            type="date" value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            style={inputStyle}
          />
          <span style={{ fontSize: '10px', color: brand.creamSubtle }}>до</span>
          <input
            type="date" value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            style={inputStyle}
          />
          {(dateFrom || dateTo) && (
            <button onClick={() => { setDateFrom(''); setDateTo(''); }}
              style={{ background: 'none', border: 'none', color: brand.creamSubtle, cursor: 'pointer', fontSize: '14px', padding: '0 2px', lineHeight: 1 }}>
              ×
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button
            onClick={downloadCSV}
            disabled={doneJobs.length === 0}
            title="Download as CSV"
            style={{ ...dlBtn, opacity: doneJobs.length === 0 ? 0.4 : 1, cursor: doneJobs.length === 0 ? 'default' : 'pointer' }}
          >
            ⬇ CSV
          </button>
          <button
            onClick={downloadAll}
            disabled={doneJobs.length === 0 || downloading}
            title="Download all results as JSON"
            style={{ ...dlBtn, opacity: doneJobs.length === 0 ? 0.4 : 1, cursor: doneJobs.length === 0 ? 'default' : 'pointer', color: brand.tealLight, borderColor: doneJobs.length > 0 ? 'rgba(78,205,196,0.3)' : brand.border }}
          >
            {downloading ? '…' : `⬇ JSON (${doneJobs.length})`}
          </button>
        </div>

      </div>

      {/* List */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {filteredJobs.length === 0 && !loading && (
          <div style={{ padding: '32px 16px', textAlign: 'center', color: brand.creamSubtle, fontSize: '13px' }}>
            {jobs.length === 0
              ? <><span>Завдань ще немає.</span><br /><span style={{ fontSize: '11px', opacity: 0.7 }}>Відправте папку Google Drive вище.</span></>
              : <span style={{ fontSize: '11px' }}>Немає завдань за вибраний період.</span>
            }
          </div>
        )}
        {filteredJobs.map(job => {
          const isSelected = job.id === selectedId;
          const isPipeline = job.pipeline === 'ocr_then_chat';
          return (
            <div key={job.id}
              onClick={() => (job.status === 'done' || job.status === 'failed') && onSelect(job)}
              style={{
                padding: '10px 16px', borderBottom: `1px solid ${brand.border}`,
                cursor: (job.status === 'done' || job.status === 'failed') ? 'pointer' : 'default',
                background: isSelected ? 'rgba(78,205,196,0.05)' : 'transparent',
                borderLeft: `3px solid ${isSelected ? brand.tealLight : 'transparent'}`,
                transition: 'background 120ms', display: 'flex', alignItems: 'center', gap: 10,
              }}
            >
              <span style={{ fontSize: '16px', flexShrink: 0 }}>{statusIcon(job.status)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                  <span style={{ fontSize: '13px', fontWeight: 500, color: brand.cream, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {shortFilename(job)}
                  </span>
                  <span style={{ fontSize: '10px', padding: '1px 5px', borderRadius: '4px', background: brand.bgMuted, color: brand.creamSubtle, flexShrink: 0 }}>
                    {job.job_type.toUpperCase()}
                  </span>
                  {isPipeline && (
                    <span style={{ fontSize: '10px', padding: '1px 5px', borderRadius: '4px', background: 'rgba(240,208,64,0.1)', color: brand.gold, border: `1px solid rgba(240,208,64,0.25)`, flexShrink: 0 }}>
                      🔗 +LLM
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '11px', color: brand.creamSubtle, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ color: statusColor(job.status) }}>{statusLabel(job.status)}</span>
                  <span style={{ opacity: 0.7 }}>{job.model.replace('MamayLM-Gemma-3-', 'Мамай ').replace('LapaLLM-Gemma-3-', 'Лапа ').replace('-IT-v2.0','').replace('-IT-v1.0','').replace('-instruct','')}</span>
                  {job.result?.usage?.total_tokens && <span>{formatTokens(job.result.usage.total_tokens)} токенів</span>}
                  {job.error && job.status !== 'failed' && <span style={{ color: brand.gold }} title={job.error}>⚠ повтор</span>}
                  <span>{timeAgo(job.created_at)}</span>
                </div>
              </div>
              {(job.status === 'done' || job.status === 'failed') && (
                <span style={{ fontSize: '11px', color: brand.creamSubtle, flexShrink: 0 }}>→</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Main BatchMode component

export function BatchMode({ apiKey }: { apiKey?: string }) {
  const [jobs, setJobs] = useState<BatchJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedJob, setSelectedJob] = useState<BatchJob | null>(null);
  const [skippedFiles, setSkippedFiles] = useState<string[]>([]);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const headers: HeadersInit = {};
      if (apiKey) headers['Authorization'] = `Bearer ${apiKey}`;
      const res = await fetch(`${CONFIG.backendUrl}/batch/jobs`, { headers });
      if (res.ok) {
        const data = await res.json();
        setJobs(data.jobs || []);
        // Update selected job if it's in the new list
        setSelectedJob(prev => {
          if (!prev) return null;
          const updated = (data.jobs as BatchJob[]).find(j => j.id === prev.id);
          return updated || prev;
        });
      }
    } catch {}
    setLoading(false);
  }, [apiKey]);

  // Auto-refresh every 5s while jobs are active
  useEffect(() => {
    fetchJobs();
    pollRef.current = setInterval(() => {
      const hasActive = jobs.some(j => j.status === 'queued' || j.status === 'processing');
      if (hasActive) fetchJobs();
    }, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchJobs]);

  // Re-check polling when jobs change
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      const hasActive = jobs.some(j => j.status === 'queued' || j.status === 'processing');
      if (hasActive) fetchJobs();
    }, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [jobs, fetchJobs]);

  const hasKey = !!apiKey;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
      {/* No key warning */}
      {!hasKey && (
        <div style={{ padding: '10px 16px', background: 'rgba(240,208,64,0.08)', borderBottom: `1px solid rgba(240,208,64,0.2)`, display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <span style={{ fontSize: '12px', color: brand.gold }}>
            ⚠ Для пакетного режиму потрібен API ключ. Встановіть його через кнопку «Ключ» у боковому меню.
          </span>
        </div>
      )}

      {/* Submit form */}
      <SubmitForm apiKey={apiKey} onSubmitted={(skipped) => {
        setSkippedFiles(skipped || []);
        setTimeout(fetchJobs, 500);
      }} />

      {/* Skipped files notice */}
      {skippedFiles.length > 0 && (
        <div style={{ padding: '8px 16px', background: 'rgba(240,208,64,0.08)', borderBottom: `1px solid rgba(240,208,64,0.2)`, display: 'flex', alignItems: 'flex-start', gap: 8, flexShrink: 0 }}>
          <span style={{ fontSize: '12px', color: brand.gold, lineHeight: 1.5 }}>
            ⚠ Пропущено {skippedFiles.length} {skippedFiles.length === 1 ? 'файл' : 'файлів'} (вже в черзі або непідтримуваний формат):
            {' '}{skippedFiles.slice(0, 3).join(', ')}{skippedFiles.length > 3 ? ` та ще ${skippedFiles.length - 3}…` : ''}
          </span>
          <button onClick={() => setSkippedFiles([])} style={{ border: 'none', background: 'none', color: brand.gold, cursor: 'pointer', fontSize: '14px', flexShrink: 0, padding: 0, lineHeight: 1 }}>×</button>
        </div>
      )}

      {/* Jobs list */}
      <JobsList
        jobs={jobs}
        loading={loading}
        onSelect={setSelectedJob}
        selectedId={selectedJob?.id ?? null}
        onRefresh={fetchJobs}
      />

      {/* Result panel — slides in from the right */}
      {selectedJob && (
        <>
          <div
            onClick={() => setSelectedJob(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 999 }}
          />
          <ResultViewer job={selectedJob} allJobs={jobs} onClose={() => setSelectedJob(null)} />
        </>
      )}
    </div>
  );
}
